/**
 * Hidden YouTube IFrame Player synced to the project playhead.
 *
 * - Audio-only experience: the iframe is positioned off-screen (volume only).
 * - Mapping: when the project playhead is at `t` ms, the song should be at
 *   `track.songStart + t` ms. So a 30-second song clip plays in real time
 *   over the matching slice of the project (no time-stretching).
 * - Beyond `track.songEnd` we pause; before project play we keep paused.
 */

import { getState, subscribe } from '../store/project';
import type { YoutubeAudioTrack } from '../types';

const API_SCRIPT = 'https://www.youtube.com/iframe_api';

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiReadyPromise: Promise<void> | null = null;
function ensureApi(): Promise<void> {
  if (apiReadyPromise) return apiReadyPromise;
  apiReadyPromise = new Promise<void>((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve();
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve();
    };
    if (!document.querySelector(`script[src="${API_SCRIPT}"]`)) {
      const s = document.createElement('script');
      s.src = API_SCRIPT;
      s.async = true;
      document.head.appendChild(s);
    }
  });
  return apiReadyPromise;
}

export function extractVideoId(input: string): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  // Already an ID
  if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) return trimmed;
  const patterns = [
    /[?&]v=([A-Za-z0-9_-]{11})/,
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    /youtube\.com\/embed\/([A-Za-z0-9_-]{11})/,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = trimmed.match(re);
    if (m && m[1]) return m[1];
  }
  return null;
}

export class YoutubePlayer {
  private host!: HTMLElement;
  private player: any = null;
  private currentVideoId: string | null = null;
  private ready = false;
  private syncTimer: number | null = null;

  constructor() {
    this.buildHost();
    subscribe((s) => {
      const tracks = s.project.audioTracks ?? (s.project.audio ? [s.project.audio] : []);
      const yt = tracks.find((t) => t.kind === 'youtube') as YoutubeAudioTrack | undefined;
      this.applyState(yt);
    });
    this.startSyncLoop();
  }

  private buildHost(): void {
    this.host = document.createElement('div');
    this.host.id = 'yt-host';
    Object.assign(this.host.style, {
      position: 'fixed',
      left: '-9999px',
      top: '-9999px',
      width: '320px',
      height: '180px',
      pointerEvents: 'none',
      zIndex: '0',
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(this.host);
  }

  private async ensurePlayer(videoId: string): Promise<void> {
    await ensureApi();
    if (!this.player) {
      const inner = document.createElement('div');
      inner.id = 'yt-inner';
      this.host.appendChild(inner);
      this.player = new window.YT.Player('yt-inner', {
        host: 'https://www.youtube-nocookie.com',
        videoId,
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          modestbranding: 1,
          rel: 0,
          fs: 0,
          playsinline: 1,
        },
        events: {
          onReady: () => {
            this.ready = true;
            this.currentVideoId = videoId;
          },
        },
      });
    } else if (this.currentVideoId !== videoId) {
      this.ready = false;
      this.player.loadVideoById({ videoId });
      this.currentVideoId = videoId;
      this.ready = true;
    }
  }

  private async applyState(audio: YoutubeAudioTrack | undefined): Promise<void> {
    if (!audio) {
      if (this.player && this.ready) {
        try {
          this.player.pauseVideo();
        } catch {
          // ignore
        }
      }
      return;
    }
    await this.ensurePlayer(audio.videoId);
  }

  private startSyncLoop(): void {
    if (this.syncTimer) return;
    this.syncTimer = window.setInterval(() => this.tick(), 100);
  }

  private tick(): void {
    if (!this.ready || !this.player) return;
    const { project, playhead, isPlaying } = getState();
    const tracks = project.audioTracks ?? (project.audio ? [project.audio] : []);
    const audio = tracks.find((t) => t.kind === 'youtube') as YoutubeAudioTrack | undefined;
    if (!audio) return;

    const songSec = (audio.songStart + playhead) / 1000;
    const songEndSec = audio.songEnd / 1000;
    const volume = computeVolume(audio, playhead, project.duration);
    try {
      this.player.setVolume(Math.round(volume * 100));
    } catch {
      // ignore
    }

    if (isPlaying && songSec < songEndSec) {
      // Seek if drift > 250ms
      try {
        const cur = this.player.getCurrentTime?.() ?? 0;
        if (Math.abs(cur - songSec) > 0.25) {
          this.player.seekTo(songSec, true);
        }
        const state = this.player.getPlayerState?.();
        if (state !== 1 /* PLAYING */) this.player.playVideo();
      } catch {
        // ignore
      }
    } else {
      try {
        const state = this.player.getPlayerState?.();
        if (state === 1) this.player.pauseVideo();
        if (!isPlaying && playhead === 0) {
          // Reset song to its start when we rewind to project 0
          this.player.seekTo(audio.songStart / 1000, true);
        }
      } catch {
        // ignore
      }
    }
  }
}

function computeVolume(audio: YoutubeAudioTrack, playhead: number, duration: number): number {
  let v = audio.volume;
  if (audio.fadeIn && playhead < audio.fadeIn) {
    v *= playhead / audio.fadeIn;
  }
  if (audio.fadeOut && playhead > duration - audio.fadeOut) {
    v *= Math.max(0, (duration - playhead) / audio.fadeOut);
  }
  return Math.max(0, Math.min(1, v));
}
